import { Badge } from '@/components/ui/badge';
import { useResponsive } from '@/hooks/use-responsive';
import { cn } from '@/lib/utils';
import type { BlogPageDataResponse } from '@/services/cms/getBlogContent/schemas';
import formatDateString from '@/utils/dates';
import { gtmKnowledgeHubLink } from '@/utils/events';
import { Link } from '@tanstack/react-router';

export interface BlogCardProps {
  blogCardData: BlogPageDataResponse;
  className?: string;
}

export const BlogCard = ({ blogCardData, className }: BlogCardProps) => {
  const { feature_image, title, date, slug, categories, excerpt } =
    blogCardData;

  const { isDesktop } = useResponsive();

  const postDate = formatDateString(date);

  return (
    <Link
      to="/blog/$slug"
      params={{
        slug: slug,
      }}
      onClick={() =>
        gtmKnowledgeHubLink({ clickText: title, clickUrl: `/blog/${slug}` })
      }
    >
      <div
        className={cn(
          'flex flex-col items-start w-full',
          'shadow-[0px_2px_8px_-2px_rgba(0,0,0,0.04)]',
          'md:min-h-[526px]',
          'group text-black',
          className
        )}
      >
        <div className="w-full shrink-0 h-[189px] md:h-[220px] bg-white border border-b-0 border-white rounded-t-3xl">
          <img
            className="w-full h-full object-cover rounded-t-3xl"
            src={feature_image?.url}
            alt={feature_image?.alt ?? ''}
            loading="lazy"
          />
        </div>
        <div className="flex flex-col w-full bg-white border border-t-0 border-grey-50 transition-all h-full rounded-b-3xl p-6 lg:p-8">
          <div className="flex-col justify-start gap-4 items-start flex">
            <Badge variant="black" size={isDesktop ? 'large' : 'medium'}>
              {categories[0]?.title ?? ''}
            </Badge>
            <div className="flex-col justify-start items-start gap-2 flex">
              <h3 className="text-lg font-nunito700 leading-7 line-clamp-3 group-hover:underline">
                {title}
              </h3>
              <p className="text-sm lg:text-base font-light line-clamp-2 mb-8 md:mb-14 lg:mb-10">
                {excerpt}
              </p>
            </div>
          </div>
          <p className="text-sm font-light leading-tight mt-auto text-dark-grey-300">
            {postDate}
          </p>
        </div>
      </div>
    </Link>
  );
};
